import Navigation from '../components/Navigation'
import ScheduleRoundRobin from '../components/ScheduleRoundRobin'
import SeasonGamesList from '../components/SeasonGamesList'
import DivisionPills, { DivisionTag } from '../components/DivisionPills'
import DivisionStandings from '../components/DivisionStandings'
import ScheduleBracket from '@/components/ScheduleBracket'
import { sf } from '../../../core/features/sf'
import { useLabels } from '../../../core/labels'
import type { SchedulePageVM } from '../../../core/pages/schedule/types'
import { useT } from '@/i18n'
import { siteClassicMessages } from '@/i18n/catalogs/site-classic'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'

// Classic Schedule view (ARCHITECTURE §6.1 C3). Data, selection, the venue
// filter and its root-venue model come from the core controller
// (useSchedulePage); the markup is today's, verbatim. The skeletons and
// empty/error states stay defined inside render, as before (§6.4).
export default function Schedule({ vm }: { vm: SchedulePageVM }) {
  const t = useLabels()
  const m = useT(siteClassicMessages).schedule
  const core = useT(siteCoreMessages)
  const featuredTournaments = vm.competitions.items
  const tournamentData = vm.competition.detail
  // #583: tag rows and tabs with their division under "All divisions" only.
  const showDivisions = vm.division.visible && vm.division.value === 'all'
  const selectedDivision = showDivisions ? featuredTournaments.find((c) => c.id === vm.competitions.value)?.division ?? null : null
  // #652: merged active seasons name each game's season in the same tag slot.
  const showSeasonGameTags = showDivisions || !!vm.seasonGames.seasonTagged
  const divisionCount = vm.noCompetitions && vm.seasonGames.status === 'ready'
    ? m.gameCount(vm.seasonGames.total, t.sportId)
    : undefined

  // Loading skeleton for tournament selector
  const TournamentSelectorSkeleton = () => (
    <div className="flex gap-2 overflow-x-auto pb-2" {...sf('state.loading')}>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-10 w-32 bg-bg-secondary/45 border border-secondary/25 rounded-lg animate-pulse"
        />
      ))}
    </div>
  )

  // Loading skeleton for schedule content
  const ScheduleSkeleton = () => (
    <div className="space-y-4" {...sf('schedule.competition.loading')}>
      <div className="h-12 bg-bg-secondary/45 border border-secondary/25 rounded-lg animate-pulse" />
      <div className="h-64 bg-bg-secondary/45 border border-secondary/25 rounded-lg animate-pulse" />
      <div className="h-32 bg-bg-secondary/45 border border-secondary/25 rounded-lg animate-pulse" />
    </div>
  )

  // Empty state when no tournaments are featured
  const NoTournamentsState = () => (
    <div className="bg-bg-secondary/45 backdrop-blur-md border border-secondary/25 rounded-lg p-8 text-center" {...sf('schedule.empty')}>
      <h2 className="text-xl font-bold text-white mb-2">{t.ui.schedule.emptyTitle}</h2>
      <p className="text-white/60 max-w-md mx-auto">
        {t.ui.schedule.emptyBody}
      </p>
    </div>
  )

  // #583: one division picked, with nothing scheduled yet.
  const DivisionEmptyState = ({ name, onAll }: { name: string; onAll: () => void }) => (
    <div className="bg-bg-secondary/45 backdrop-blur-md border border-secondary/25 rounded-lg p-8 text-center">
      <h2 className="text-xl font-bold text-white mb-2">{m.divisionEmpty(name)}</h2>
      <button type="button" onClick={onAll} className="min-h-[44px] text-sm font-bold text-primary hover:underline underline-offset-4">
        {m.seeAllDivisions} →
      </button>
    </div>
  )

  // Error state
  const ErrorState = ({ message, onRetry, marker, title }: { message: string; onRetry?: () => void; marker: string; title?: string }) => (
    <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6 text-center">
      <div className="text-red-400 text-4xl mb-4">⚠️</div>
      <h3 className="text-lg font-bold text-red-400 mb-2">{title ?? t.ui.common.errorTitle}</h3>
      <p className="text-white/60 mb-4">{message}</p>
      {onRetry && (
        <button
          {...sf(marker)}
          onClick={onRetry}
          className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 rounded-lg font-bold text-sm transition-colors"
        >
          {t.ui.common.retry}
        </button>
      )}
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-b from-secondary-dark via-bg-primary to-bg-primary">
      <Navigation />

      <div className="container mx-auto px-4 py-8 pt-24 lg:pt-28">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-white uppercase tracking-tight">
            {core.pages.nav.schedule}
          </h1>
          <p className="text-white/60 mt-2">
            {t.ui.schedule.subtitle}
          </p>
        </div>

        {/* #583: division pill row (renders nothing without 2+ live divisions) */}
        <DivisionPills division={vm.division} className="-mt-2 mb-8" count={divisionCount} showStatus />

        {/* #652: season picker (a sport with 2+ public seasons): today's view or one season, finished ones included */}
        {vm.season.visible && (
          <div className="-mt-2 mb-8 flex items-center gap-3" {...sf('schedule.season-filter')}>
            <label htmlFor="season-filter" className="text-sm font-bold text-white/60">
              {t.ui.schedule.seasonPicker}
            </label>
            <select
              id="season-filter"
              value={vm.season.value === null ? '' : String(vm.season.value)}
              onChange={(e) => vm.season.set(e.target.value ? parseInt(e.target.value, 10) : null)}
              className="bg-bg-secondary/60 border border-secondary/30 text-white text-base leading-5 sm:text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-primary/50"
            >
              {vm.season.options.map((o) => (
                <option key={o.value ?? 'current'} value={o.value === null ? '' : String(o.value)}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Tournament Selector */}
        {vm.status === 'loading' ? (
          <div className="mb-8">
            <TournamentSelectorSkeleton />
          </div>
        ) : vm.status === 'error' ? (
          <div className="mb-8">
            <ErrorState
              message={vm.error ?? ''}
              onRetry={vm.retry}
              marker="state.error"
            />
          </div>
        ) : vm.noCompetitions ? (
          // #478: games outside any competition replace the empty state.
          vm.seasonGames.total > 0 ? <SeasonGamesList seasonGames={vm.seasonGames} withHeading={false} showDivision={showSeasonGameTags} />
            : vm.seasonGames.status === 'loading' ? <ScheduleSkeleton />
            : vm.division.visible && vm.division.active ? <DivisionEmptyState name={vm.division.active.name} onAll={() => vm.division.set('all')} />
            : <NoTournamentsState />
        ) : (
          <>
            {/* Tournament tabs (only show if multiple) */}
            {featuredTournaments.length > 1 && (
              <div className="mb-8">
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" {...sf('schedule.competitions')}>
                  {featuredTournaments.map((tournament) => (
                    <button
                      key={tournament.id}
                      {...sf('schedule.competition-tab')}
                      onClick={() => vm.competitions.set(tournament.id)}
                      className={`px-4 py-2 rounded-lg font-bold text-sm whitespace-nowrap transition-all flex-shrink-0 ${
                        vm.competitions.value === tournament.id
                          ? 'bg-primary text-black'
                          : 'bg-bg-secondary/45 text-white/60 hover:text-white border border-secondary/25 hover:border-primary/30'
                      }`}
                    >
                      {tournament.name}
                      <span className="ml-2 text-xs opacity-80">{tournament.year}</span>
                      {showDivisions && tournament.division && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider opacity-80">{tournament.division.name}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tournament Name (when single tournament) */}
            {featuredTournaments.length === 1 && tournamentData && (
              <div className="mb-6" {...(vm.competition.status === 'ready' ? sf('schedule.competition') : {})}>
                <h2 className="text-xl font-bold text-primary">
                  {tournamentData.name}
                  {/* Skip the year when the name already carries it (#199). */}
                  {tournamentData.showYear && (
                    <span className="ml-2 text-white/60 text-sm font-normal">
                      {tournamentData.seasonYear}
                    </span>
                  )}
                </h2>
              </div>
            )}

            {/* Tournament Content */}
            {vm.competition.status === 'loading' ? (
              <ScheduleSkeleton />
            ) : vm.competition.notFound ? (
              // #418: a linked competition that doesn't exist.
              <ErrorState title={t.ui.schedule.competitionNotFound} message={t.ui.schedule.competitionNotFoundBody} marker="state.not-found" />
            ) : vm.competition.status === 'error' ? (
              <ErrorState
                message={vm.competition.error ?? ''}
                // #414: really refetches the selected competition.
                onRetry={vm.competition.retry}
                marker="schedule.competition.error"
              />
            ) : tournamentData ? (
              <div {...(vm.bracket ? sf('schedule.bracket') : {})}>
                {/* Tournament header info */}
                {featuredTournaments.length > 1 && (
                  <div className="mb-6 flex flex-wrap items-center gap-4" {...sf('schedule.competition')}>
                    <h2 className="text-xl font-bold text-white">
                      {tournamentData.name}
                    </h2>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                        tournamentData.type === 'round_robin'
                          ? 'bg-secondary/50 text-primary border border-secondary'
                          : 'bg-primary/20 text-primary border border-primary/30'
                      }`}>
                        {t.format(tournamentData.type)}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                        tournamentData.status === 'active'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : tournamentData.status === 'completed'
                          ? 'bg-white/10 text-white/60 border border-secondary/30'
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      }`}>
                        {t.competitionProgress({ status: tournamentData.status, gamesStarted: tournamentData.gamesStarted, startsAt: tournamentData.startLabel })}
                      </span>
                      {selectedDivision && <DivisionTag division={selectedDivision} />}
                    </div>
                  </div>
                )}

                {/* Public note */}
                {tournamentData.note && (
                  <div className="bg-primary/5 border border-primary/20 rounded-lg px-4 py-3 mb-4" {...sf('schedule.competition.note')}>
                    <p className="text-white/80 text-sm">{tournamentData.note}</p>
                  </div>
                )}

                {/* Venue filter — only shown when this tournament's games
                    span more than one root venue. */}
                {vm.venueFilter.visible && (
                  <div className="mb-4 flex items-center gap-3" {...sf('schedule.venue-filter')}>
                    <label htmlFor="venue-filter" className="text-sm font-bold text-white/60">
                      {m.venue}
                    </label>
                    <select
                      id="venue-filter"
                      value={vm.venueFilter.value === null ? '' : vm.venueFilter.value.toString()}
                      onChange={(e) => vm.venueFilter.set(e.target.value ? parseInt(e.target.value, 10) : null)}
                      className="bg-bg-secondary/60 border border-secondary/30 text-white text-base leading-5 sm:text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-primary/50"
                    >
                      <option value="">{core.pages.schedule.allVenues}</option>
                      {vm.venueFilter.options.slice(1).map((v) => (
                        <option key={v.value} value={String(v.value)}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Render appropriate schedule view */}
                {tournamentData.type === 'round_robin' ? (
                  <ScheduleRoundRobin
                    games={vm.raw.games}
                    teams={vm.raw.details!.teams}
                    matchdays={vm.raw.details!.matchdays}
                  />
                ) : (
                  <ScheduleBracket
                    games={vm.raw.games}
                    teams={vm.raw.details!.teams}
                    autoAdvance={tournamentData.autoAdvance}
                  />
                )}

                {/* Tournament dates if available */}
                {(tournamentData.startDate || tournamentData.endDate) && (
                  <div className="mt-6 pt-6 border-t border-secondary/30" {...sf('schedule.competition.dates')}>
                    <div className="flex flex-wrap gap-4 text-sm text-white/50">
                      {tournamentData.startDate && (
                        <div>
                          <span className="text-white/30 mr-2">{m.starts}</span>
                          {tournamentData.startLabel}
                        </div>
                      )}
                      {tournamentData.endDate && (
                        <div>
                          <span className="text-white/30 mr-2">{m.ends}</span>
                          {tournamentData.endLabel}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
            {/* #478: the season's games outside any competition, below it. */}
            <SeasonGamesList seasonGames={vm.seasonGames} withHeading showDivision={showSeasonGameTags} />
          </>
        )}

        {/* #583: stacked division standings, one table per division (2+ live divisions only).
            Mounted once everything above it has its final height, so the tables never get
            pushed down by games arriving above them (a layout shift on phones). */}
        {/* #652: they're today's tables, so not under a picked season. */}
        {vm.division.visible && vm.season.value == null && vm.status === 'ready' && vm.competition.status !== 'loading' && vm.seasonGames.status !== 'loading' && (
          <DivisionStandings stacked={vm.division.value === 'all'} />
        )}
      </div>
    </div>
  )
}
